import { CSSProperties } from "react";

const BULB_COLORS = ["#C0272D", "#F2C14E", "#1F7A4D"];
const BULB_COUNT = 8;
const RING_SIZE = 56;
const BULB_SIZE = 10;
const RING_RADIUS = 22;

const bulbs = Array.from({ length: BULB_COUNT }, (_, i) => ({
    color: BULB_COLORS[i % BULB_COLORS.length],
    opacity: Math.max(0.25, 1 - i * 0.1),
    angle: i * (360 / BULB_COUNT),
}));

const styles: Record<string, CSSProperties> = {
    backdrop: {
        position: "fixed",
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 99,
        backgroundColor: "rgba(0, 0, 0, 0.4)",
    },
    card: {
        position: "fixed",
        zIndex: 99,
        top: "50%",
        left: "50%",
        transform: "translate(-50%,-50%)",
        display: "flex",
        columnGap: "20px",
        alignItems: "center",
        justifyContent: "center",
        boxSizing: "border-box",
        padding: "22px 32px",
        background: "#FFFBF2",
        border: "3px solid #E0A526",
        borderRadius: "18px",
        boxShadow: "0 24px 60px rgba(0,0,0,0.45)",
    },
    holly: {
        position: "absolute",
        top: -20,
        right: 18,
        width: 60,
        height: 41,
    },
    ringWrap: {
        position: "relative",
        width: RING_SIZE,
        height: RING_SIZE,
        flex: "0 0 auto",
    },
    ring: {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
    },
    star: {
        position: "absolute",
        top: 16,
        left: 16,
        width: 24,
        height: 24,
    },
    textWrap: {
        display: "flex",
        flexDirection: "column",
        gap: 2,
    },
    title: {
        fontSize: 17,
        fontWeight: 700,
        color: "#1E2B25",
    },
    subtitle: {
        fontSize: 12,
        color: "#5B6B63",
    },
};

export const Loading = () => {
    return (
        <>
            <style>{`
                @keyframes umis-loading-rotate {
                    from { transform: rotate(0deg); }
                    to   { transform: rotate(360deg); }
                }
                @keyframes umis-loading-pulse {
                    0%, 100% { transform: scale(0.85); opacity: 0.8; }
                    50%      { transform: scale(1.1);  opacity: 1; }
                }
                .umis-loading-ring  { animation: umis-loading-rotate 1.6s linear infinite; }
                .umis-loading-star  { animation: umis-loading-pulse 1.6s ease-in-out infinite; }
                @media (prefers-reduced-motion: reduce) {
                    .umis-loading-ring,
                    .umis-loading-star { animation: none; }
                }
            `}</style>

            <div style={styles.backdrop} aria-hidden="true"></div>

            <div style={styles.card} role="status" aria-live="polite">
                {/* Holly */}
                <svg aria-hidden="true" viewBox="0 0 76 52" style={styles.holly}>
                    <ellipse cx="22" cy="26" rx="20" ry="9" transform="rotate(-28 22 26)" fill="#1F7A4D" />
                    <ellipse cx="52" cy="26" rx="20" ry="9" transform="rotate(28 52 26)" fill="#17603C" />
                    <circle cx="35" cy="30" r="6" fill="#D6303A" />
                    <circle cx="44" cy="34" r="6" fill="#B3202A" />
                    <circle cx="39" cy="40" r="6" fill="#D6303A" />
                </svg>

                {/* Spinner: ring of Christmas lights around a pulsing star */}
                <div style={styles.ringWrap}>
                    <div className="umis-loading-ring" style={styles.ring} aria-hidden="true">
                        {bulbs.map((b, i) => (
                            <span
                                key={i}
                                style={{
                                    position: "absolute",
                                    top: (RING_SIZE - BULB_SIZE) / 2,
                                    left: (RING_SIZE - BULB_SIZE) / 2,
                                    width: BULB_SIZE,
                                    height: BULB_SIZE,
                                    borderRadius: "50%",
                                    background: b.color,
                                    opacity: b.opacity,
                                    transform: `rotate(${b.angle}deg) translateY(-${RING_RADIUS}px)`,
                                }}
                            />
                        ))}
                    </div>
                    <svg
                        className="umis-loading-star"
                        aria-hidden="true"
                        viewBox="0 0 48 48"
                        style={styles.star}
                    >
                        <polygon
                            points="24,2 30,17 46,18 33.5,28.5 37.5,44 24,35.5 10.5,44 14.5,28.5 2,18 18,17"
                            fill="#E0A526"
                        />
                    </svg>
                </div>

                <div style={styles.textWrap}>
                    <span style={styles.title}>Loading...</span>
                    <span style={styles.subtitle}>Wrapping things up for you</span>
                </div>
            </div>
        </>
    );
};